<script setup lang="ts">
import { ref, computed, onMounted, type PropType } from 'vue'
import DialogRevamp from '@/components/DialogRevamp.vue'
import OTPWithMessage from '@/components/ui/input/OTPWithMessage.vue'
import TextButton from '@/components/TextButton.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import useAuthDialog from '@/lib/useAuthDialog'
import injectStrict from '@/lib/inject'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { requestEmailOtp, verifyEmailOtp } from '@/assets/ts/apiManagement'
import { createGAEvent } from '@/assets/ts/customEvent'
import { getCurrentGaPageName } from '@/lib/utils'

enum TypeOtp {
	Login = 'login',
	Signup = 'signup',
	Forget = 'forget',
}

const props = defineProps({
	email: {
		type: String,
		default: '',
	},
	type: {
		type: String as PropType<`${TypeOtp}`>,
		default: TypeOtp.Login,
	},
})

const textContent: any = injectStrict('textContent')
const { isShowDialog } = useAuthDialog(hideDialogDirectly)
const emit = defineEmits([
	'hide',
	'backStep',
	'backStepForget',
	'onOtpSubmitLogin',
	'onOtpSubmitSignup',
	'onOtpSubmitForget',
	'toLogin',
])

const defalutCountDown = 60
const isExceedRequestLimit = ref(false)
const countDown = ref(defalutCountDown)
const otpValue = ref('')
const messageApiOtpInvalid = ref('')
const messageApiOtpInvalidSpecial = ref('')
const otpToken = ref('')
const isLoading = ref(false)
const pageName = getCurrentGaPageName()

const isTypeLogin = computed(() => props.type === TypeOtp.Login)
const isTypeSignup = computed(() => props.type === TypeOtp.Signup)
const currentTitle = computed(() => {
	if (isTypeLogin.value) {
		return textContent.login_one_time_password_title
	}
	return textContent.verify_email_title
})
const currentSubtitle = computed(() => {
	if (isTypeLogin.value) {
		return textContent.login_one_time_password_dialog.replace(
			'{email}',
			props.email,
		)
	}
	return textContent.verify_email_dialog.replace('{email}', props.email)
})
const canResendOtp = computed(() => !countDown.value)
const resendButtonType = computed(() =>
	canResendOtp.value ? 'primary' : 'disabled',
)
const currentResendButtonText = computed(() => {
	const text = isTypeLogin.value
		? textContent.login_one_time_password_resend
		: textContent.verify_email_resend
	const canClickResendOtp = isExceedRequestLimit.value || canResendOtp.value
	const replaceRange = canClickResendOtp ? '({time})' : '{time}'
	const replaceText = canClickResendOtp ? '' : countDown.value

	return text.replace(replaceRange, replaceText)
})
const isSubmitDisabled = computed(
	() => otpValue.value.length !== 6 || isExceedRequestLimit.value,
)
const currentButtonText = computed(() =>
	isTypeLogin.value ? textContent.button_login : textContent.button_next_step,
)
const isOtpError = computed(() => messageApiOtpInvalid.value !== '')

function backStep() {
	if (props.type === TypeOtp.Forget) {
		emit('backStepForget')
	} else {
		emit('backStep')
	}
}

function resetCountDown() {
	countDown.value = defalutCountDown
}

function startCountDown() {
	const timer = setInterval(() => {
		countDown.value -= 1
		if (countDown.value === 0) {
			clearInterval(timer)
		}
	}, 1000)
}

async function submit() {
	if (isSubmitDisabled.value) return
	resetErrorMessage()
	try {
		await verifyOtp()
		const form = {
			type: 'otp',
			token: otpToken.value,
		}
		if (isTypeLogin.value) {
			emit('onOtpSubmitLogin', form)
		} else if (isTypeSignup.value) {
			emit('onOtpSubmitSignup', form)
		} else {
			emit('onOtpSubmitForget', form)
		}
	} catch (error) {
		console.log(error)
	}
}

function resetOtpValue() {
	otpValue.value = ''
	otpToken.value = ''
}

function resetErrorMessage() {
	messageApiOtpInvalid.value = ''
	messageApiOtpInvalidSpecial.value = ''
}

async function requestOtp() {
	resetErrorMessage()
	resetOtpValue()
	resetCountDown()
	startCountDown()
	return requestEmailOtp(props.email).catch(error => {
		if (error.key === '429_TOO_MANY_REQUESTS') {
			isExceedRequestLimit.value = true
			messageApiOtpInvalidSpecial.value = error.message
		} else {
			messageApiOtpInvalid.value = error.message
		}
		throw error
	})
}

async function verifyOtp() {
	isLoading.value = true
	return verifyEmailOtp({
		email: props.email,
		otp: otpValue.value,
	})
		.then(res => {
			if (isTypeLogin.value) {
				createGAEvent('otp_login_success', {
					...(pageName && { page_name: pageName }),
				})
			} else if (isTypeSignup.value) {
				createGAEvent('sign_up_otp_input_passed', {
					...(pageName && { page_name: pageName }),
				})
			}
			otpToken.value = res.token
		})
		.catch(error => {
			isLoading.value = false
			if (error.key === '410_GONE') {
				messageApiOtpInvalidSpecial.value = error.message
			} else {
				messageApiOtpInvalid.value = error.message
			}
			throw error
		})
}

function toLogin() {
	emit('toLogin')
}

function clickRequestOtp() {
	if (isTypeLogin.value) {
		createGAEvent('resend_login_otp', {
			...(pageName && { page_name: pageName }),
		})
	} else if (isTypeSignup.value) {
		createGAEvent('resend_sign_up_otp', {
			...(pageName && { page_name: pageName }),
		})
	}

	requestOtp()
}

function hideDialogDirectly() {
	emit('hide')
}

onMounted(() => {
	requestOtp()
})
</script>

<template>
	<DialogRevamp
		v-model="isShowDialog"
		:isFullInMobile="true"
		:isShowDefaultClose="false"
		:isCustomOverlayFunction="true"
		:isSecondLayer="true"
		:size="'sm'"
		@overlayClick="backStep"
	>
		<template #header>
			<div class="bs-auth-back-header">
				<button class="bs-icon bs-auth-back-header__back" @click="backStep">
					<BaseIcon :name="'back'" class="h-16px w-16px fill-n-950" />
				</button>
				<h5 class="bs-auth__title">
					{{ currentTitle }}
				</h5>
			</div>
		</template>
		<template #content>
			<div class="bs-auth-otp__content">
				<BaseAttentionBox
					v-if="messageApiOtpInvalidSpecial"
					class="bs-auth-otp__content__attention-box"
					:icon="'error'"
					:background="'error'"
				>
					<template #description>
						{{ messageApiOtpInvalidSpecial }}
					</template>
				</BaseAttentionBox>

				<div class="bs-auth-otp__content__subtitle">
					{{ currentSubtitle }}
				</div>

				<OTPWithMessage
					v-model="otpValue"
					:isError="isOtpError"
					:errorMessage="messageApiOtpInvalid"
					:disabled="isExceedRequestLimit"
				/>

				<TextButton
					class="bs-auth-otp__content__countdown"
					:text="currentResendButtonText"
					:type="resendButtonType"
					@onClick="clickRequestOtp"
				></TextButton>
			</div>
		</template>
		<template #footer>
			<BaseButton
				class="bs-auth-otp__content__submit"
				:size="'large'"
				:isFull="true"
				:isDisabled="isSubmitDisabled"
				:isLoading="isLoading"
				@click="submit"
			>
				{{ currentButtonText }}
			</BaseButton>

			<TextButton
				v-if="isTypeLogin"
				class="bs-auth-otp__content__use-password"
				:text="textContent.login_switch_to_password"
				:type="'secondary'"
				@onClick="toLogin"
			></TextButton>
		</template>
	</DialogRevamp>
</template>
